import type { Nutrients } from '@/utils/nutrients'

import type {
  Announcement,
  AppMessage,
  DietType,
  Message,
  State,
  User,
  UserFlag,
  UserIcons,
} from './types'

import '@mobily/ts-belt'

import { A, D, F, G, O, pipe } from '@mobily/ts-belt'
import * as Sentry from '@sentry/vue'
import { defineStore } from 'pinia'

import { stringify } from '@repo/utils'

import { universityService } from '@/services/universityService'
import { userService } from '@/services/userService'
import { allNutriens } from '@/utils/nutrients'
import { reportError } from '@/utils/reportError'

export const useGlobalStore = defineStore('global', {
  actions: {
    changeDietCounter({ dietType, value }: { dietType: DietType; value: number }) {
      if (this.user && dietType !== 'patient') {
        this.user.counters.diets[dietType] += value
      }
    },
    changeDishCounter({ value }: { value: number }) {
      if (this.user) {
        this.user.counters.dishes += value
      }
    },
    changeIngredientCounter({ value }: { value: number }) {
      if (this.user) {
        this.user.counters.ingredients += value
      }
    },
    checkSavedChosenNutrientsList() {
      let savedChosenNutrientsList: Nutrients[] = this.chosenNutrients
      const storageNutrients = localStorage.getItem('chosenNutrients')
      if (storageNutrients) {
        try {
          if (storageNutrients.length > 0) {
            const parsedNutrients = JSON.parse(storageNutrients) as Nutrients[]
            savedChosenNutrientsList = pipe(
              O.fromPredicate(parsedNutrients, G.isArray),
              O.getWithDefault([] as Nutrients[]),
            )
          }
        } catch {
          // vue2 to vue3 migration
          savedChosenNutrientsList = storageNutrients
            .split(',')
            .filter(item => allNutriens.includes(item as Nutrients)) as Nutrients[]
        }
        this.setChosenNutrients(savedChosenNutrientsList)
      }
    },
    closeMessage(messageIndex: number) {
      this.messages = this.messages.splice(messageIndex, 1)
    },
    createMessage({
      type = 'info',
      title,
      content = '',
      timeout = 5000,
    }: Partial<Omit<AppMessage, 'id'>> & { title: string }) {
      this.appMessagesCounter += 1
      const id = this.appMessagesCounter

      this.appMessages.push({
        content,
        id,
        timeout,
        title,
        type,
      })
      if (timeout) {
        setTimeout(() => {
          this.deleteMessage(id)
        }, timeout)
      }
      return id
    },
    deleteAnnouncementByIndex(announcementIndex: number) {
      const announcements = this.announcements
      announcements.splice(announcementIndex, 1)
      this.announcements = announcements
    },
    deleteAnnouncements() {
      this.announcements = this.announcements.splice(0, this.announcements.length)
    },
    deleteAnnouncementsByIconName(iconName: IconName) {
      this.announcements = this.announcements.filter(
        announcement => announcement.iconName !== iconName,
      )
    },
    deleteMessage(id: number) {
      this.appMessages = this.appMessages.filter(message => message.id !== id)
    },
    deleteMessages() {
      this.appMessages = []
    },
    enabledIconsNamesAdd(iconName: UserIcons) {
      if (!this.enabledIconsNames.includes(iconName)) {
        this.enabledIconsNames.push(iconName)
      }
    },
    enabledIconsNamesDelete(iconName: UserIcons) {
      this.enabledIconsNames = this.enabledIconsNames.filter(name => name !== iconName)
    },
    hideAppUpgradePlanModal() {
      this.appUpgradePlanModal = false
    },
    hideModal() {
      this.nameOfVisibleModal = ''
    },
    incrementDishesPrivateCount() {
      if (this.user) {
        this.user.dishes_private_count++
      }
    },

    // REFACTOR New Actions
    incrementPrintedDiets() {
      if (this.user) {
        this.user.printed_diets++
      }
    },
    pushAnnouncement(announcement: Announcement) {
      const announcements = this.announcements
      const inevitableLossesList = announcements.filter(a => a.iconName === 'delete_sweep')
      if (announcement.iconName === 'delete_sweep') {
        if (inevitableLossesList.length > 0) {
          return
        } else {
          announcements.push(announcement)
        }
      } else {
        announcements.push(announcement)
      }
      this.announcements = announcements
    },
    pushMessages(message: Message) {
      this.messages.push(message)
    },
    setChosenNutrients(nutrients: Nutrients[]) {
      this.chosenNutrients = nutrients

      localStorage.setItem('chosenNutrients', stringify(nutrients))
    },
    /**
     * Displays info and error messages
     * @param  {String} options.type    Type of message, should be 'info' or 'error'
     * @param  {String} options.title   Message title
     * @param  {String} options.content Message content
     * @param  {Number} options.timeout Timeout after what message should be deleted, pass null for never
     * @return {Number}                 Returning message id for update/delete
     */
    setSearchedPhrase(value: string) {
      this.searchedPhrase = value
    },
    setUser({ user }: { user: User }) {
      this.user = user

      const scope = Sentry.getCurrentScope()
      scope.setExtra('plan_name', user.current_plan_name)
      scope.setUser({
        email: user.email,
        id: user.id,
      })
      if (G.isNotNullable(dataLayer) && G.isArray(dataLayer)) {
        const data = {
          account_type: user.account_type,
          event: 'account_type_update',
        }
        dataLayer.push(data)
      }
    },
    shiftMessages() {
      this.messages = this.messages.filter(message => message.type !== 'toast')
    },
    showAppUpgradePlanModal() {
      this.appUpgradePlanModal = true
    },
    showedIconsNamesAdd(iconName: UserIcons) {
      if (!this.showedIconsNames.includes(iconName)) {
        this.showedIconsNames.push(iconName)
      }
    },
    showedIconsNamesDelete(iconName: UserIcons) {
      this.showedIconsNames = this.showedIconsNames.filter(name => name !== iconName)
    },
    showModal(value: string) {
      this.nameOfVisibleModal = value
    },
    toggleFavoriteIngredient(ingredientId: number) {
      if (this.user) {
        const favoriteIngredients = this.user.favourite_ingredients
        const ingredientIndex = favoriteIngredients.indexOf(ingredientId)
        if (ingredientIndex === -1) {
          favoriteIngredients.push(ingredientId)
          this.createMessage({ title: 'Dodano produkt do ulubionych' })
        } else {
          favoriteIngredients.splice(ingredientIndex, 1)
          this.createMessage({ title: 'Usunięto produkt z ulubionych' })
        }
      }
    },
    toggleLeftMenuCollapsed() {
      this.isLeftMenuCollapsed = !this.isLeftMenuCollapsed
      localStorage.setItem('isLeftMenuCollapsed', this.isLeftMenuCollapsed.toString())
    },
    async updateLeaderStudents() {
      try {
        const response = await universityService.fetchLeaderStudents()
        this.leaderStudents = response.data
      } catch (err) {
        reportError(err, 'Error while fetching leader students')
      }
    },
    updateMessage({
      id,
      title = '',
      content = '',
    }: {
      content?: string
      id: number
      title?: string
    }) {
      A.forEach(this.appMessages, message => {
        if (message.id === id) {
          if (title) {
            message.title = title
          }
          if (content) {
            message.content = content
          }
        }
      })
    },
    updateUserData(data: Partial<User>) {
      this.user = D.merge(this.user, data)
    },
    async updateUserFlag(flag: UserFlag, value = true) {
      if (this.user?.user_flags) {
        try {
          await userService.updateFlag(flag, value)
          this.user.user_flags = value
            ? this.user.user_flags.filter(f => f !== flag)
            : F.toMutable(A.uniq([...this.user.user_flags, flag]))
        } catch (err) {
          reportError(err, `Error while updating user flag ${flag}`)
        }
      }
    },
    // END OF REFACTOR New Actions
  },
  getters: {
    getPossessiveAdjective: () => (number: number, adjective: string) => {
      if (adjective === 'go') {
        if (number > 1) {
          return 'je'
        } else {
          return adjective
        }
      }
      return ''
    },
    hasAddDishPermission(state) {
      const dishesPrivateCount = state.user?.dishes_private_count
      if (!state.user?.is_superuser && dishesPrivateCount) {
        if (this.hasPerm('accounts.max_dish_bo')) {
          return true
        } else if (this.hasPerm('accounts.max_dish_2000')) {
          if (dishesPrivateCount > 1999) {
            return false
          }
        } else if (this.hasPerm('accounts.max_dish_1500')) {
          if (dishesPrivateCount > 1499) {
            return false
          }
        } else if (this.hasPerm('accounts.max_dish_1000')) {
          if (dishesPrivateCount > 999) {
            return false
          }
        } else if (this.hasPerm('accounts.max_dish_10')) {
          if (dishesPrivateCount > 9) {
            return false
          }
        }
      }
      return true
    },
    hasPerm: state => (permission: string) => {
      const permissions = state.user?.permissions ?? []
      return permissions.includes(permission)
    },
    isDietListSystemDietEnabled(state) {
      const userPlanName = state.plan_name
      return ['basic', 'master', 'premium', 'vip'].includes(userPlanName)
    },
    isIconEnabled: state => (iconName: UserIcons) => state.enabledIconsNames.includes(iconName),

    isIconShowed: state => (iconName: UserIcons) => state.showedIconsNames.includes(iconName),
    leaderOptions(state) {
      return state.leaderStudents
        .filter(obj => {
          return state.user?.email === obj.student_email
        })
        .map(obj => {
          return {
            text: `${obj.leader_email} (${obj.leader_first_name} ${obj.leader_last_name})`,
            value: obj.leader_email,
          }
        })
    },
    messagesForDisplay: state => {
      // clone messages for prevent changing state
      return [...state.messages].reverse()
    },
  },
  state: (): State => ({
    announcements: [],
    appMessages: [],
    appMessagesCounter: 0,
    appUpgradePlanModal: false,
    chosenNutrients: ['protein', 'fat', 'carbohydrates', 'fiber', 'carbohydrate_exchanger'],
    enabledIconsNames: [],
    errorElement: false,
    errorGlobal: false,
    errorView: false,
    isLeftMenuCollapsed: false,
    leaderStudents: [],
    messages: [],
    // New fields
    nameOfVisibleModal: '',
    plan_name: 'basic',
    searchedPhrase: '',
    shareDietsButtonEnabled: true,
    showedIconsNames: [],
    user: undefined,
  }),
})
